import {
  WIDGET_STATE,
  SESSION_TYPES,
  BOT_ROUTING,
  BOT_STATUS,
  MESSAGE_TYPES,
} from '@/enums'
import Api from '@/plugins/api'
import dayjs from 'dayjs'

const initialState = () => ({
  autocloseParameters: {
    enabled: false,
    timeout: 0, // second
    timeoutBeforeWarning: 0, // second
    messages: [],
  },
  cssLoaded: {
    fullframe: false,
    teaser: false,
    editor: false,
  },
  chatReport: false,
  css: {
    fullframe: null,
    teaser: null,
    print: null,
    editor: null,
    satisfaction: null,
    chatPrequalification: null,
    botPrequalification: null,
    sidebar: null,
    initialDisplayMode: '',
  },
  externalIdentity: null,
  lastTargetRule: null,
  lastVisitTimeStamp: null,
  fullframeOptions: {
    agentAvatarUrl: null,
    botAvatarUrl: null,
    draggable: false,
    draggableButtonActive: false,
    resizable: false,
    width: 0,
    height: 400,
    minWidth: 300,
    minHeight: 400,
    maxWidth: Infinity,
    maxHeight: Infinity,
    persistencyContactSid: 0, // in day
    persistencyChatboxVisibility: 0, // in second
    realCloseChatDelay: null, // in second
    sendChatByMail: false,
    surveyEnabled: false,
    uploadFile: false,
    print: false,
    closableByCustomer: false,
    displayChatHistoryConversation: false,
    displayCustomerHistoryBrowsing: false,
    displayNewMessageOnTitleTabChatbox: false,
    displayGdprPreview: false,
    displayInteractionTimestamp: false,
    displayInteractionTimestampOption: null,
    displayRealTimeTyping: false,
    liveChatPrequalificationActive: false,
    liveChatPrequalificationSurveyId: null,
    sidebarWidth: null,
    sidebarHeight: null,
    restrictedFileMimeTypes: null,
    restrictedFileSize: 0, // in MB
  },
  device: null,
  shiftInitiated: false,
  shiftInitial: {
    top: 0,
    left: 0,
  },
  shift: {
    top: 0,
    left: 0,
  },
  teaserOptions: {
    displayNewMessageOnMinimizeChatbox: false,
    hideTeaserOnScrollDown: false,
    animateTeaserOnScrollDown: false,
    showAvatar: false,
    urlAvatar: null,
    closableByCustomer: false,
  },
  widgetState: WIDGET_STATE.INIT,
  rgpd: {
    visible: false,
    acceptance: false,
    enable: false,
  },
  bot: {
    automaticHandoffAgentToBot: false,
    handoffAgentToBotKnowledge: null,
    botConfigAttributes: null,
    botConfigName: null,
    botProvider: null,
    botConversationRouting: null,
    botLocaleCode: null,
    botPrequalificationActive: null,
    botPrequalificationSurveyId: null,
    botSessionDuration: 0, // in second
    botEscalationBtnActive: false,
    botHomeBtnActive: false,
    botHomeBtnKnowledge: null,
    autocompleteNumCharacters: 0,
    autocompleteNumResults: null, // when null then display all available results
    suggestEscalationForInsatisf: false,
    knowledgeToCall: null,
  },
  closeWidget: {
    closeBy: null,
    process: false,
    timestamp: null,
    lastAgentMessage: {
      sid: null,
      timestamp: null,
    },
  },
  sessionType: null,
  isFallingBackToBot: false,
  sessionTypeIsChanging: false,
  draggableReady: false,
  chatInApp: false,
})

export const state = initialState()

export const getters = {
  getCss: (state) => (name) => state.css[name],
  conversationIsReady: (state, getters, rootState, rootGetters) => {
    return !getters.isRgpdVisible && !rootGetters['PreQualification/isShow']
  },
  /**
   * Return true when widget is visibile but not active (opening bot conversation, livechat, satisfaction)
   *
   * @param {*} state
   * @param {*} getters
   * @param {*} rootState
   */
  widgetIsInactive(state, getters, rootState, rootGetters) {
    if (
      ![WIDGET_STATE.TEASER, WIDGET_STATE.FULLFRAME].includes(
        state.widgetState,
      ) ||
      state.closeWidget.process
    ) {
      return false
    }

    switch (state.sessionType) {
      case SESSION_TYPES.CHAT:
        return !rootGetters['LiveChat/readyToChat']

      case SESSION_TYPES.BOT:
        return !rootState.Bot.botInProgress && !rootState.Bot.botHandoff

      case SESSION_TYPES.SATISFACTION:
      case SESSION_TYPES.QUEUING:
        return false

      default:
        break
    }
  },
  closeProcessingDelay(state) {
    const now = dayjs()
    return now.isBefore(state.closeWidget.timestamp)
      ? dayjs(state.closeWidget.timestamp).diff(now, 'second')
      : 0
  },
  closeDelay(state, getters, rootState, rootGetters) {
    if (rootGetters['PreQualification/isShow'] || getters.isRgpdVisible) {
      // close immediately if prequalification or rgpd is displayed
      return 0
    }

    return (
      getters.closeProcessingDelay ||
      state.fullframeOptions.realCloseChatDelay ||
      0
    )
  },
  displayChatHistoryConversation(state) {
    return state.fullframeOptions.displayChatHistoryConversation
  },
  isWidgetClosing(state) {
    return state.closeWidget.process
  },
  isChatSession(state) {
    return state.sessionType === SESSION_TYPES.CHAT
  },
  isBotSession(state) {
    return state.sessionType === SESSION_TYPES.BOT
  },
  isSatisfactionSession(state) {
    return state.sessionType === SESSION_TYPES.SATISFACTION
  },
  isQueuingSession(state) {
    return state.sessionType === SESSION_TYPES.QUEUING
  },
  isRgpdVisible(state) {
    return state.rgpd.enable && !state.rgpd.acceptance
  },
  escalationDissatisfactionKnowledge: (state) => {
    return state.bot.suggestEscalationForInsatisf
      ? state.bot.knowledgeToCall
      : null
  },
}

export const mutations = {
  UPDATE_WIDGET_STATE(state, payload) {
    state.widgetState = payload
  },
  UPDATE_SESSION_TYPE(state, payload) {
    state.sessionType = payload
  },
  UPDATE_SESSION_BOT_FALLBACK(state, payload) {
    state.isFallingBackToBot = payload
  },
  UPDATE_SESSION_TYPE_IS_CHANGING(state, payload) {
    state.sessionTypeIsChanging = payload
  },
  UPDATE_CHAT_REPORT(state, payload) {
    state.chatReport = payload
  },
  UPDATE_CSS(state, payload) {
    state.css = { ...state.css, ...payload }
  },
  UPDATE_CSS_LOADED(state, type) {
    if (type in state.cssLoaded) {
      state.cssLoaded[type] = true
    }
  },
  UPDATE_BOT_OPTIONS(state, payload) {
    state.bot = { ...state.bot, ...payload }
  },
  UPDATE_FULLFRAME_OPTIONS(state, payload) {
    state.fullframeOptions = {
      ...state.fullframeOptions,
      ...payload,
      // Do not overload avatar url if already set
      agentAvatarUrl:
        state.fullframeOptions.agentAvatarUrl || payload.agentAvatarUrl,
      botAvatarUrl: state.fullframeOptions.botAvatarUrl || payload.botAvatarUrl,
    }
  },
  UPDATE_TEASER_OPTIONS(state, payload) {
    state.teaserOptions = { ...state.teaserOptions, ...payload }
  },
  UPDATE_AUTOCLOSE_PARAMETERS(state, payload) {
    state.autocloseParameters = {
      ...state.autocloseParameters,
      ...payload,
    }
  },
  SET_RGPD_ENABLE_STATUS(state, isEnable = false) {
    state.rgpd.enable = isEnable
  },
  UPDATE_RGPD(state, { visible = true, acceptance }) {
    state.rgpd = {
      visible,
      acceptance,
      enable: state.rgpd.enable,
    }
  },
  UPDATE_DEVICE(state, payload) {
    state.device = payload
  },
  UPDATE_SHIFT(state, payload = true) {
    state.shift = { ...payload }
  },
  UPDATE_SHIFT_INITIATED(state) {
    state.shiftInitiated = true
  },
  SET_SHIFT_INITIAL(state, payload) {
    state.shiftInitial = { ...payload }
  },
  UPDATE_EXTERNAL_IDENTITY(state, payload) {
    state.externalIdentity = payload
  },
  UPDATE_LAST_VISIT_TIMESTAMP(state) {
    state.lastVisitTimeStamp = new Date()
  },
  UPDATE_LAST_TARGET_RULE(state, payload) {
    state.lastTargetRule = payload
  },
  UPDATE_CLOSE_WIDGET(state, payload) {
    state.closeWidget.closeBy = payload
    state.closeWidget.process = true
  },
  UPDATE_CLOSE_TIMESTAMP(state, payload) {
    state.closeWidget.timestamp = payload
  },
  UPDATE_DRAGGABLE_READY(state, payload = false) {
    state.draggableReady = payload
  },
  UPDATE_WIDGET_WARNING_DATA(state, payload) {
    state.closeWidget.lastAgentMessage = payload
  },
  UPDATE_WIDGET_WARNING_TIMESTAMP(state, timestamp) {
    state.closeWidget.lastAgentMessage.timestamp = timestamp
  },
  RESET_CLOSE_WIDGET(state) {
    state.closeWidget.closeBy = null
    state.closeWidget.process = false
    state.closeWidget.timestamp = null
  },
  RESET(state) {
    const newState = initialState()
    Object.keys(newState).forEach((key) => {
      if (!['externalIdentity', 'lastVisitTimeStamp', 'rgpd'].includes(key)) {
        state[key] = newState[key]
      }
    })
  },
}

export const actions = {
  /**
   * Reset state
   * @param {*} { commit }
   */
  reset({ commit }) {
    commit('RESET')
  },
  /**
   * @description Get widget configuration from backend
   */
  async getWidgetConfiguration(
    { commit, state, dispatch },
    restoreToInitialState = false,
  ) {
    const { externalIdentity } = state
    const { data } = await Api.chat.getWidgetConfiguration()

    const isPageReload =
      externalIdentity &&
      !restoreToInitialState &&
      state.widgetState !== WIDGET_STATE.INIT

    const restoreTriggered = !!(
      restoreToInitialState || state.widgetState === WIDGET_STATE.INIT
    )

    if (!externalIdentity) {
      await dispatch('getExternalIdentity')
    } else if (state.chatInApp && !state.lastVisitTimeStamp) {
      commit('UPDATE_LAST_VISIT_TIMESTAMP')
    }

    if (isPageReload) {
      // keep previous widget size
      delete data.data.fullframe.width
      delete data.data.fullframe.height
    }

    if (restoreTriggered) {
      dispatch('initRgpdStatus', data.data.fullframe.displayGdprPreview)
      dispatch('setWidgetFirstSessionType', data.data)
    }

    commit('UPDATE_AUTOCLOSE_PARAMETERS', data.data.autocloseParameters)
    commit('UPDATE_TEASER_OPTIONS', data.data.teaser)
    commit('UPDATE_FULLFRAME_OPTIONS', data.data.fullframe)
    commit('UPDATE_BOT_OPTIONS', data.data.bot)
    commit('UPDATE_CSS', data.data.css)
    commit('UPDATE_CHAT_REPORT', data.data.chatReport)
  },
  /**
   * @description get external user identity
   * @param {*} { commit, state }
   */
  async getExternalIdentity({ commit, state }) {
    if (!state.externalIdentity) {
      const { data } = await Api.chat.getExternalIdentity()
      commit('UPDATE_EXTERNAL_IDENTITY', data.data)
    }
    commit('UPDATE_LAST_VISIT_TIMESTAMP')
  },
  async setWidgetFirstSessionType(
    { state, commit, dispatch, rootState },
    { bot },
  ) {
    let firstSession =
      bot.botProvider && bot.botConversationRouting === BOT_ROUTING.BOT_FIRST
        ? SESSION_TYPES.BOT
        : SESSION_TYPES.CHAT

    if (firstSession === SESSION_TYPES.CHAT && bot.botProvider) {
      await dispatch('LiveChat/checkAgentAvailability', false, {
        root: true,
      })
      if (!rootState.LiveChat.agentAvailable) {
        firstSession = SESSION_TYPES.BOT
      }
    }

    dispatch('updateSessionType', firstSession)
    console.debug(`First sessionType: ${state.sessionType}`)
  },
  sessionTypeFallback({ state, commit, dispatch, rootState }) {
    if (
      state.bot.botProvider &&
      state.bot.botConversationRouting === BOT_ROUTING.IF_NO_AGENT &&
      getters.isChatSession &&
      rootState.Bot.botStatus !== BOT_STATUS.DOWN
    ) {
      console.debug(`sessionTypeFallback: ${SESSION_TYPES.BOT}`)
      commit('UPDATE_SESSION_BOT_FALLBACK', true)
      dispatch('updateSessionType', SESSION_TYPES.BOT)
    }
  },
  updateSessionType({ state, commit }, payload) {
    if (payload !== state.sessionType && !state.sessionTypeIsChanging) {
      commit(
        'UPDATE_SESSION_TYPE_IS_CHANGING',
        // These types correspond to components load in App.vue
        [SESSION_TYPES.BOT, SESSION_TYPES.CHAT, SESSION_TYPES.QUEUING].includes(
          payload,
        ),
      )
      commit('UPDATE_SESSION_TYPE', payload)
    }
  },
  /**
   * On reload reevaluate rgpd configuration
   * Visible depends on admin option and previous acceptance
   * Acceptance is false by default then preserved until contact SID expire
   *
   * @param {Boolean} gdprIsEnable
   */
  initRgpdStatus({ state, commit }, gdprIsEnable) {
    commit('SET_RGPD_ENABLE_STATUS', gdprIsEnable)
    commit('UPDATE_RGPD', {
      visible: gdprIsEnable && !state.rgpd.acceptance,
      acceptance: state.rgpd.acceptance,
    })
  },
  setInitialState({ state, commit }) {
    const initialDisplayMode = state.css.initialDisplayMode
    commit('UPDATE_WIDGET_STATE', WIDGET_STATE[initialDisplayMode])
  },
  setLastAgentMessage({ state, commit }, message) {
    if (!message?.attributes) return
    if (message.attributes.incoming) {
      return commit('UPDATE_WIDGET_WARNING_DATA', {
        sid: null,
        timestamp: null,
      })
    }

    const isLiveChatMessage =
      message.attributes.type === MESSAGE_TYPES.LIVE_CHAT

    const isMessageAlreadyHandled =
      message.sid === state.closeWidget.lastAgentMessage.sid

    if (!isLiveChatMessage || isMessageAlreadyHandled) return

    return commit('UPDATE_WIDGET_WARNING_DATA', {
      sid: message.sid,
      timestamp: Date.now(),
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
